'use client';
import React from 'react';
import { MobileMenu as SharedMobileMenu } from '@launchstore/shared-puck';
import type { MobileMenuProps, SharedMobileMenuItem } from '@launchstore/shared-puck';
import type { EnrichedMenuItem } from '@/lib/menu/types';
import type { Theme } from '@/lib/theme/api';

/**
 * Storefront wrapper for MobileMenu.
 *
 * WHY THIS SHAPE
 * The shared `MobileMenu` is a Puck `ComponentConfig` (object with
 * `.render`), NOT a React component. The previous version of this file
 * exported a bare `React.FC` and rendered `<SharedMobileMenu .../>` as
 * JSX — both wrong (the FC can't be registered in a Puck config, and the
 * JSX treats a config object as a component → "React error #130").
 *
 * This file mirrors the working `MenuNavigation.tsx` wrapper: spread the
 * shared config, override only `render`, and delegate to the shared
 * `.render(...)` after projecting storefront data into the shared shape.
 *
 * DATA: the (main) layout server-injects `items` (EnrichedMenuItem[])
 * and `theme` into this component's puckData node via
 * `injectMenuDataIntoTemplate` in layout.tsx.
 */
const projectItem = (item: EnrichedMenuItem): SharedMobileMenuItem => ({
  id: item.id,
  label: item.label,
  url: item.enrichedData?.resolvedUrl || item.url || (item.entityId ? `/${item.type}s/${item.entityId}` : '#'),
  openInNewTab: item.openInNewTab,
  children: (item.children || []).map((c) => projectItem(c)),
  megaMenu: item.megaMenu?.enabled ? {
    enabled: true,
    subcategoryLimit: item.megaMenu.subcategoryLimit,
    subcategories: (item.enrichedData?.category?.subcategories || []).slice(0, item.megaMenu.subcategoryLimit || 12).map((s: any) => ({
      id: s.id,
      label: s.name,
      url: `/categories/${s.handle}`,
    })),
  } : undefined,
});

const projectTheme = (theme: Theme): { background?: string; text?: string; border?: string; subText?: string; accordionBackground?: string; titleFontSize?: string; padding?: string; itemPadding?: string; } => {
  const tokens = (theme?.globalSettings?.colors?.tokens || {}) as any;
  const t = tokens.mobileMenu || {};
  const typography = (theme?.globalSettings?.typography || {}) as any;
  const spacing = (theme?.globalSettings as any)?.spacing?.mobileMenu || {};
  return {
    background: t.background,
    text: t.text,
    border: t.border,
    subText: t.subText,
    accordionBackground: t.accordionBackground,
    titleFontSize: typography.fontSize?.navigationMobile,
    padding: spacing.padding,
    itemPadding: spacing.itemPadding,
  };
};

export interface StorefrontMobileMenuProps extends Omit<MobileMenuProps, 'items' | 'theme' | 'isOpen'> {
  items: EnrichedMenuItem[];
  theme: Theme;
  isOpen: boolean;
}

const SharedMobileMenuRender = (SharedMobileMenu as any).render;

const MobileMenuImpl: React.FC<StorefrontMobileMenuProps> = (props: any) => {
  const { items, theme, ...rest } = props;
  const projected = (items || []).map((it: EnrichedMenuItem) => projectItem(it));
  const projectedTheme = theme ? projectTheme(theme) : undefined;
  return <SharedMobileMenuRender {...(rest as any)} items={projected} theme={projectedTheme} />;
};

export const MobileMenu: any = { ...SharedMobileMenu, render: (props: any) => <MobileMenuImpl {...props} /> };
export default MobileMenu;
